import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DatasetService } from '../../../../shared/dataset/service/dataset.service';
import { AlertService } from '../../../../shared/alert/alert.service';
import { NgbActiveModal, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { RCallDto, RPackageService } from '../../services/r-package.service';
import { KeyValuePipe, NgForOf, NgIf } from '@angular/common';
import { ObservationVariable } from '../../../../shared/model/observation-variable.model';
import { PlotVisualizationImageDisplayComponent } from './plot-visualization-image-display/plot-visualization-image-display.component';
import { SubscriptionsManager } from '../../../../shared/util/subscriptions-manager';
import { HttpErrorResponse } from '@angular/common/http';

@Component({
    selector: 'jhi-plot-visualization-modal',
    templateUrl: './plot-visualization-modal.component.html',
    styleUrl: './plot-visualization-modal.component.scss'
})
export class PlotVisualizationModalComponent implements OnInit, OnDestroy {

    constructor(private datasetService: DatasetService,
                private alertService: AlertService,
                private modalService: NgbModal,
                private activeModal: NgbActiveModal,
                private rPackageService: RPackageService) {
    }

    ngOnDestroy(): void {
        this.subscriptionManager.unsubscribeAll();
    }

    @Input() searchComposite: any;
    @Input() studyId: number;
    @Input() datasetId: number;

    subscriptionManager = new SubscriptionsManager();

    OBSERVATION_UNIT_ID = 8201;
    PLOT_TYPES = {
        SCATTERPLOT: 'Scatterplot',
        BOXPLOT: 'Boxplot',
        HISTOGRAM: 'Histogram'
    };

    isExporting: boolean = false;
    rCalls = [];
    selectedRCall: RCallDto;
    selectedVariableX: ObservationVariable = null;
    selectedVariableY: ObservationVariable = null;
    variates: ObservationVariable[] = [];
    factors: ObservationVariable[] = [];
    regressionMethods = [{
        method: '"auto"',
        description: 'study-editor.plot-visualization-modal.regression-method.auto'
    }, {
        method: '"lm"',
        description: 'study-editor.plot-visualization-modal.regression-method.linear.model'
    }, {
        method: '"glm"',
        description: 'study-editor.plot-visualization-modal.regression-method.generalized.linear.model'
    }, {
        method: '"gam"',
        description: 'study-editor.plot-visualization-modal.regression-method.generalized.additive.model'
    }, {
        method: '"loess"',
        description: 'study-editor.plot-visualization-modal.regression-method.loess.regression'
    }];

    ngOnInit(): void {
        this.initialize();
    }

    selectedRCallChanged() {
        this.selectedVariableX = null;
        this.selectedVariableY = null;
    }

    initialize() {
        const qplotPackageId = 3;
        this.rPackageService.getRCallsObjects(qplotPackageId).toPromise().then((data: any) => {
            this.rCalls = data;
            this.selectedRCall = data[0];
        });
        this.datasetService.getObservationSetColumns(this.studyId, this.datasetId, this.searchComposite.draftMode).toPromise().then((columnsDataResponse) => {

            const columnsData = columnsDataResponse.body;
            const variateVariableTypes = ['TRAIT', 'SELECTION_METHOD'];

            this.variates = columnsData.filter(column => {
                return variateVariableTypes.includes(column.variableType?.toString());
            });
            this.factors = columnsData.filter(column => {
                return !variateVariableTypes.includes(column.variableType?.toString()) && this.OBSERVATION_UNIT_ID !== column.termId;
            });
        });
    };

    cancel() {
        this.subscriptionManager.unsubscribeAll();
        this.activeModal.close();
    };

    async generate() {
        if (this.validate(this.selectedRCall, this.selectedVariableX, this.selectedVariableY)) {
            this.isExporting = true;
            const rCall = this.prepareParameters(structuredClone(this.selectedRCall), this.selectedVariableX, this.selectedVariableY);
            this.searchComposite.visibleColumns = this.getVisibleColumns(this.selectedRCall, this.selectedVariableX, this.selectedVariableY);
            const data = await this.datasetService.getObservationForVisualization(this.studyId, this.datasetId, this.searchComposite).toPromise();
            rCall.parameters.data = JSON.stringify(data);

            this.subscriptionManager.add = this.rPackageService.executeRCall(rCall.endpoint, rCall.parameters).subscribe((response) => {
                const imageUrl = response.headers.get('location') + 'graphics/1/svg';
                this.showImage(imageUrl);
            }, (error: HttpErrorResponse) => {
                if (error.status === 400) {
                    this.alertService.error('study-editor.plot-visualization-modal.error.plot.graph.generation');
                } else {
                    this.alertService.error('study-editor.plot-visualization-modal.error.plot.graph.generic.message');
                }
                this.isExporting = false;
            });
        };
    }

    showImage(imageUrl: string) {
        const modalRef = this.modalService.open(PlotVisualizationImageDisplayComponent as Component, { size: 'xxxl', backdrop: 'static' });
        modalRef.componentInstance.imageUrl = imageUrl;
    }

    validate(rCall, variableX, variableY) {
        if (!this.hasRequiredFields(rCall, variableX, variableY)) {
            this.alertService.error('study-editor.plot-visualization-modal.error.plot.graph.fill.in.all.required.fields');
            return false;
        }
        return true;
    }

    hasRequiredFields(rCall, variableX, variableY) {
        if (this.PLOT_TYPES.SCATTERPLOT === rCall.description) {
            return variableX && variableY && rCall.parameters.method;
        } else if (this.PLOT_TYPES.HISTOGRAM === rCall.description) {
            return variableX;
        } else if (this.PLOT_TYPES.BOXPLOT === rCall.description) {
            return variableX && variableY;
        }
    }

    getVisibleColumns(rCall, selectedVariableX, selectedVariableY) {
        // Only the variables selected from the UI should be included in the data that will be sent to OpenCPU.
        const visibleColumns = [];
        visibleColumns.push(selectedVariableX.name);
        if ('Histogram' !== rCall.description) {
            visibleColumns.push(selectedVariableY.name);
        }
        return visibleColumns;
    }

    prepareParameters(rCall, selectedVariableX, selectedVariableY) {
        // Make sure that field names are wrapped in backticks (`) to escape the spaces in them if there's any.
        // If plot graph is a Boxplot, we must wrap the X field name with R 'factor' function
        // so that R will know that the field is a factor
        rCall.parameters.x = this.PLOT_TYPES.BOXPLOT === rCall.description ? 'factor(' + this.wrapTextWith('`', selectedVariableX.name) + ')' : this.wrapTextWith('`', selectedVariableX.name);
        // Set x and y labels with variable alias
        rCall.parameters.xlab = this.wrapTextWith('"', selectedVariableX.alias);
        if (this.PLOT_TYPES.HISTOGRAM !== rCall.description) {
            rCall.parameters.y = this.wrapTextWith('`', selectedVariableY.name);
            rCall.parameters.ylab = this.wrapTextWith('"', selectedVariableY.alias);
        }
        return rCall;
    }

    wrapTextWith(character, text) {
        return character + text + character;
    }

}
